import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { PricingItem } from '@/types'

export interface PricingCalculationLine {
  item_type: string
  description: string
  quantity: number
  unit_price: number
  amount: number
}

export interface PricingCalculationResult {
  items: PricingCalculationLine[]
  subtotal_before_percentage: number
  total: number
}

interface PricingCalculationRequest {
  pricing_formula: PricingItem[]
  unit_price: number
  consumption: Record<string, number>
  // EP-58 R1: condominium fields. Required when the formula has any
  // per_sqm or per_share_ratio entry; safe to omit otherwise.
  unit_area_sqm?: number
  unit_share_ratio?: number
}

/**
 * usePricingCalculation — fetch the canonical pricing breakdown from the server.
 *
 * EP-56 L1c — Step 2 wizard preview goes through POST /api/pricing_calculation
 * (backed by Ruby PricingEngine), so preview = invoice is mathematically
 * guaranteed by a single computation path. No more JS computeBreakdown.
 *
 * Includes a 400ms debounce on input changes: rapid formula edits (e.g.
 * typing a new rate) don't spam the endpoint, and the user sees a
 * skeleton state for at most 400ms when they pause typing.
 *
 * @param formula     The pricing formula array (PricingItem[])
 * @param unitPrice   The rent amount (always emitted as one row)
 * @param consumption Per-meter usage map ({"water": 10, "electricity": 50})
 * @param enabled     Skip the request entirely when false (default true)
 */
export function usePricingCalculation(
  formula: PricingItem[],
  unitPrice: number,
  consumption: Record<string, number>,
  enabled: boolean = true,
  unitAreaSqm?: number,
  unitShareRatio?: number,
) {
  const [debouncedKey, setDebouncedKey] = useState({
    formula, unitPrice, consumption, unitAreaSqm, unitShareRatio,
  })

  // Debounce 400ms — architect-flagged latency mitigation. Rapid typing in
  // the formula editor won't trigger a request until the user pauses.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKey({ formula, unitPrice, consumption, unitAreaSqm, unitShareRatio })
    }, 400)
    return () => clearTimeout(timer)
  }, [formula, unitPrice, consumption, unitAreaSqm, unitShareRatio])

  return useQuery<PricingCalculationResult>({
    queryKey: ['pricing_calculation', debouncedKey],
    queryFn: async () => {
      const body: PricingCalculationRequest = {
        pricing_formula: debouncedKey.formula,
        unit_price: debouncedKey.unitPrice,
        consumption: debouncedKey.consumption,
        ...(debouncedKey.unitAreaSqm !== undefined && { unit_area_sqm: debouncedKey.unitAreaSqm }),
        ...(debouncedKey.unitShareRatio !== undefined && { unit_share_ratio: debouncedKey.unitShareRatio }),
      }
      const { data } = await api.post<PricingCalculationResult>('/api/pricing_calculation', body)
      return data
    },
    enabled,
    staleTime: 60_000,
  })
}
